import { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Spin,
  Layout,
  Typography,
  Form,
  Input,
  Button,
  Select,
  Modal,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import api from '../../service/api';
import * as S from './styles';
import InputMask from 'react-input-mask';

const { Option } = Select;
const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 20 },
};

const { Title } = Typography;
const { Content } = Layout;

export type SaveNewClientForm = {
  id: string;
  empresa: string;
  cnpj: string;
  administradora: string;
  banco: string;
  agencia: string;
  conta: string;
};

interface MyRecord {
  id: string;
  empresa: string;
  cnpj: string;
  administradora: string;
  campo1: string;
  campo2: string;
  campo3: string;
}

interface LocationState {
  record: MyRecord | null;
}

const CadastroPortalAdministradoras: React.FC = () => {
  const [isSpinning, setIsSpinning] = useState(false);
  const [empresas, setEmpresas] = useState<Array<{ cnpj: string; empresa: string }>>([]);
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { record } = location.state || { record: null };
  const [form] = Form.useForm();
  const [pluggyToken, setPluggyToken] = useState<string | null>(null);

  useEffect(() => {
    if (record) {
      form.setFieldsValue(record);
    }
  }, [record, form]);

  const fetchEmpresas = async () => {
    setIsSpinning(true);
    try {
      const clienteJSON = localStorage.getItem('cliente');
      if (!clienteJSON) {
        toast.error('Cliente não encontrado no localStorage');
        return;
      }

      const cliente = JSON.parse(clienteJSON);
      const nomeDoGrupo = cliente.nomeDoGrupo || "";

      const response = await api.get(`/api/LoginPortal/empresasCNPJ`, {
        params: { groupName: nomeDoGrupo },
      });

      //Aqui
      if (response.data && response.data.result && response.data.result.length > 0) {
        setEmpresas(response.data.result);
      } else {
        toast.error('Nenhuma empresa encontrada.');
      }
    } catch (error) {
    } finally {
      setIsSpinning(false);
    }
  };

  useEffect(() => {
    fetchEmpresas();
  }, []);

  const handleEmpresaChange = (value: string) => {
    const selectedEmpresa = empresas.find(empresa => empresa.empresa === value);

    if (selectedEmpresa) {
      form.setFieldsValue({ cnpj: selectedEmpresa.cnpj });
    }
  };

  const removeFormatting = (value: string) => {
    return value.replace(/\D/g, ''); // Remove tudo que não é número
  };

  const onFinish = async (values: SaveNewClientForm) => {

    const formattedValues = {
      ...values,
      cnpj: removeFormatting(values.cnpj),
    };

    setIsSpinning(true);
    try {
      const dadosEmpresa = {
        Empresa: formattedValues.empresa,
        CNPJ: formattedValues.cnpj,
        Banco: formattedValues.banco,
        Agencia: formattedValues.agencia,
        Conta: formattedValues.conta,
      };

      const response = await api.post('Extratos/cadastrarContasExtratosBancarios', dadosEmpresa);

      handleBack();
    } catch (error) {
      console.error('Erro inesperado:', error);
      toast.error('Um erro inesperado aconteceu.');
    } finally {
      setIsSpinning(false);
    }
  };

  const antIcon = <LoadingOutlined style={{ fontSize: 34, color: "#2ce414" }} spin />;
  const handleBack = () => {
    const storedUser = localStorage.getItem('cliente');
    if (storedUser) {
      const clienteObject = JSON.parse(storedUser);
      if (clienteObject) {
        history.push({
          pathname: `/ListaContas`,
          state: { id: clienteObject.id }, // Passando o id pelo estado
        });
      }
    }
  };

  return (
    <Spin indicator={antIcon} spinning={isSpinning}>
      <Layout className="layout">
        <Content style={{
          backgroundColor: "white",
          padding: '50px 50px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          height: '100vh',
        }}>
          <S.SiteLayoutContent>
            <S.ContainerTitle>
              <Title level={3}>Cadastro de Contas Correntes</Title>
            </S.ContainerTitle>
            <S.FormContainer>
              <Form
                {...layout}
                form={form}
                name="nest-messages"
                onFinish={onFinish}
              >
                <Form.Item
                  name="empresa"
                  label="EMPRESA"
                  rules={[{ required: true, message: 'Por favor, selecione uma empresa' }]}
                >
                  <Select placeholder="Selecione uma empresa" onChange={handleEmpresaChange}>
                    {empresas.length > 0 ? (
                      empresas.map(empresa => (
                        <Option key={empresa.cnpj} value={empresa.empresa}>
                          {empresa.empresa}
                        </Option>
                      ))
                    ) : (
                      <Option disabled>Nenhuma empresa encontrada</Option>
                    )}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="cnpj"
                  label="CNPJ"
                  rules={[{ required: true, message: 'Por favor, insira o CNPJ' }]}
                >
                  <InputMask mask="99.999.999/9999-99" readOnly={true} style={{ userSelect: 'none', pointerEvents: 'none' }} />
                </Form.Item>
                <Form.Item
                  name='banco'
                  label="BANCO"
                  rules={[{ required: true, message: 'Por favor insira o nome do banco' }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name='agencia'
                  label="AGÊNCIA"
                  rules={[{ required: true, message: 'Por favor insira o nome da agência' }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name='conta'
                  label="CONTA"
                  rules={[{ required: true, message: 'Por favor insira a conta' }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item wrapperCol={{ span: 24 }}>
                  <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: '15px' }}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      disabled={isSpinning}
                      style={{
                        backgroundColor: '#4CAF50',
                        borderColor: '#4CAF50',
                        color: 'white',
                        marginRight: '8px',
                        borderRadius: '4px',
                      }}
                    >
                      Salvar
                    </Button>
                    <Button
                      type="default"
                      htmlType="button"
                      onClick={handleBack}
                      style={{
                        backgroundColor: '#f44336',
                        borderColor: '#f44336',
                        color: 'white',
                        borderRadius: '4px',
                      }}
                    >
                      Voltar
                    </Button>
                  </div>
                </Form.Item>
              </Form>
            </S.FormContainer>
          </S.SiteLayoutContent>
        </Content>
      </Layout>
    </Spin>
  );
};

export default CadastroPortalAdministradoras;